/**
 * DailyPlanningWizard
 *
 * 4-step morning planning wizard. Auto-triggered once per day.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X, Check, Clock, Calendar, Info, ChevronRight } from 'lucide-react';
import type { EventInstance, TimeFormat, CalendarSettings } from '../types';
import type { SchedulableTaskDetail } from '@/lib/api';

interface DailyPlanningWizardProps {
  open: boolean;
  onClose: () => void;
  yesterdayEvents: EventInstance[];
  todayEvents: EventInstance[];
  incompleteTasks: SchedulableTaskDetail[];
  unscheduledTasks: SchedulableTaskDetail[];
  availableHours: number;
  scheduledHours: number;
  todayPriorities: string[];
  setTodayPriorities: (priorities: string[]) => void;
  onCompleteTask: (taskId: string) => Promise<void>;
  onScheduleTask: (taskId: string, hour: number, durationMinutes: number) => void;
  workingHoursStart: number;
  workingHoursEnd: number;
  onDismiss: () => void;
  timeFormat?: TimeFormat;
  stepToggles: Pick<
    CalendarSettings,
    'showReviewYesterday' | 'showTodaysSchedule' | 'showPlanYourDay' | 'showSetYourFocus'
  >;
  onUpdateSettings: (update: Partial<CalendarSettings>) => void;
}

const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

function formatTimeLocal(dateStr: string, timeFormat: TimeFormat = '12h'): string {
  return new Date(dateStr).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: timeFormat === '12h',
  });
}

function CapacityBar({ scheduled, available }: { scheduled: number; available: number }) {
  const pct = available > 0 ? (scheduled / available) * 100 : 0;
  const color = pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>
          {scheduled.toFixed(1)}h scheduled of {available}h available
        </span>
        <span>{Math.max(0, available - scheduled).toFixed(1)}h remaining</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

/** Setting key for each wizard step index. */
const STEP_SETTING_KEYS: (keyof Pick<
  CalendarSettings,
  'showReviewYesterday' | 'showTodaysSchedule' | 'showPlanYourDay' | 'showSetYourFocus'
>)[] = ['showReviewYesterday', 'showTodaysSchedule', 'showPlanYourDay', 'showSetYourFocus'];

const STEP_TOGGLE_LABELS = [
  'Show Review Yesterday',
  "Show Today's Schedule",
  'Show Plan Your Day',
  'Show Set Your Focus',
];

export function DailyPlanningWizard({
  open,
  onClose,
  yesterdayEvents,
  todayEvents,
  incompleteTasks,
  unscheduledTasks,
  availableHours,
  scheduledHours,
  todayPriorities,
  setTodayPriorities,
  onCompleteTask,
  onScheduleTask,
  workingHoursStart,
  workingHoursEnd,
  onDismiss,
  timeFormat = '12h',
  stepToggles,
  onUpdateSettings,
}: DailyPlanningWizardProps) {
  const [step, setStep] = useState(0);
  const [priorityInput, setPriorityInput] = useState('');
  const [localPriorities, setLocalPriorities] = useState<string[]>(todayPriorities);
  const [schedulingTaskId, setSchedulingTaskId] = useState<string | null>(null);
  const [yesterdayEventsOpen, setYesterdayEventsOpen] = useState(false);
  const [scheduledTaskHours, setScheduledTaskHours] = useState<Record<string, number>>({});
  const [localScheduledExtra, setLocalScheduledExtra] = useState(0);

  /** Ordered indices of steps that are enabled. */
  const enabledSteps = useMemo(() => {
    const enabled: number[] = [];
    if (stepToggles.showReviewYesterday) enabled.push(0);
    if (stepToggles.showTodaysSchedule) enabled.push(1);
    if (stepToggles.showPlanYourDay) enabled.push(2);
    if (stepToggles.showSetYourFocus) enabled.push(3);
    return enabled;
  }, [stepToggles]);

  /** The actual step index (0-3) for the current position in the enabled list. */
  const currentStepIndex = enabledSteps[step] ?? 0;
  const isLastStep = step >= enabledSteps.length - 1;
  const totalEnabledSteps = enabledSteps.length;

  const goNext = useCallback(() => {
    if (!isLastStep) setStep((s) => s + 1);
  }, [isLastStep]);

  const goBack = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  // Reset state when dialog opens
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleClose();
    }
  };

  const handleClose = () => {
    onDismiss();
    onClose();
    setStep(0);
    setSchedulingTaskId(null);
    setScheduledTaskHours({});
    setLocalScheduledExtra(0);
  };

  const handleDone = () => {
    setTodayPriorities(localPriorities);
    handleClose();
  };

  const totalScheduled = scheduledHours + localScheduledExtra;

  const workingHourOptions = useMemo(() => {
    const hours: number[] = [];
    for (let h = workingHoursStart; h < workingHoursEnd; h++) {
      hours.push(h);
    }
    return hours;
  }, [workingHoursStart, workingHoursEnd]);

  const handleScheduleConfirm = (taskId: string, hour: number, task: SchedulableTaskDetail) => {
    const duration = task.estimatedMinutes || 60;
    onScheduleTask(taskId, hour, duration);
    setScheduledTaskHours((prev) => ({ ...prev, [taskId]: hour }));
    setLocalScheduledExtra((prev) => prev + duration / 60);
    setSchedulingTaskId(null);
  };

  const addPriority = () => {
    const trimmed = priorityInput.trim();
    if (trimmed && localPriorities.length < 3 && !localPriorities.includes(trimmed)) {
      setLocalPriorities([...localPriorities, trimmed]);
      setPriorityInput('');
    }
  };

  const removePriority = (index: number) => {
    setLocalPriorities(localPriorities.filter((_, i) => i !== index));
  };

  const stepTitles = ['Review yesterday', "Today's schedule", 'Plan your day', 'Set your focus'];
  const stepDescriptions = [
    'Review what happened yesterday — check off incomplete tasks and see how your time was spent.',
    'See everything on your calendar today and how much of your available time is already booked.',
    "Pick time slots for tasks that aren't scheduled yet so nothing falls through the cracks.",
    "Jot down up to 3 priorities as a note to yourself. They'll be shown again in your evening review so you can reflect on how the day went.",
  ];

  /** Toggle row shown at the bottom of each step to disable it. */
  const stepToggle = (
    <div className="flex items-center justify-between gap-3 rounded-md border border-dashed px-3 py-2 shrink-0">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[12px] text-muted-foreground leading-none">
          {STEP_TOGGLE_LABELS[currentStepIndex]}
        </span>
        <span className="relative group">
          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
          <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 rounded-md bg-popover border px-2.5 py-1.5 text-[11px] font-normal text-popover-foreground shadow-md opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
            This step can be re-enabled in Settings &rarr; Plan My Day.
          </span>
        </span>
      </div>
      <Switch
        checked={stepToggles[STEP_SETTING_KEYS[currentStepIndex]]}
        onCheckedChange={(checked) =>
          onUpdateSettings({ [STEP_SETTING_KEYS[currentStepIndex]]: checked })
        }
        className="shrink-0"
      />
    </div>
  );

  // If all steps are disabled, just close
  if (open && enabledSteps.length === 0) {
    handleClose();
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg h-[28rem] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {stepTitles[currentStepIndex]}
            <span className="relative group">
              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 rounded-md bg-popover border px-3 py-2 text-xs font-normal text-popover-foreground shadow-md opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                {stepDescriptions[currentStepIndex]}
              </span>
            </span>
          </DialogTitle>
          <DialogDescription>
            Step {step + 1} of {totalEnabledSteps}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Step 0: Review yesterday */}
          {currentStepIndex === 0 && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Unfinished tasks</h4>
                {incompleteTasks.length === 0 ? (
                  <p className="text-sm text-muted-foreground">None recorded.</p>
                ) : (
                  <div className="space-y-2 space-y-1">
                    {incompleteTasks.map((task) => (
                      <label
                        key={task.id}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <Checkbox
                          onCheckedChange={(checked) => {
                            if (checked) onCompleteTask(task.id);
                          }}
                        />
                        <span>{task.title}</span>
                        <Badge
                          variant="outline"
                          className={`text-xs ${PRIORITY_COLORS[task.priority]}`}
                        >
                          {task.priority}
                        </Badge>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-medium w-full text-left"
                  onClick={() => setYesterdayEventsOpen(!yesterdayEventsOpen)}
                >
                  <ChevronRight
                    className={`h-4 w-4 text-muted-foreground transition-transform ${yesterdayEventsOpen ? 'rotate-90' : ''}`}
                  />
                  Yesterday's events
                  <span className="text-xs text-muted-foreground ml-1">
                    ({yesterdayEvents.filter((e) => !e.isAllDay).length})
                  </span>
                </button>
                {yesterdayEventsOpen && (
                  <div className="space-y-1 space-y-1 mt-2 ml-5">
                    {yesterdayEvents.filter((e) => !e.isAllDay).length === 0 ? (
                      <p className="text-sm text-muted-foreground">No events yesterday.</p>
                    ) : (
                      yesterdayEvents
                        .filter((e) => !e.isAllDay)
                        .sort(
                          (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
                        )
                        .map((evt) => (
                          <div key={evt.id} className="flex items-center gap-2 text-sm py-1">
                            <Clock className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="text-muted-foreground w-24 shrink-0">
                              {formatTimeLocal(evt.startAt, timeFormat)} –{' '}
                              {formatTimeLocal(evt.endAt, timeFormat)}
                            </span>
                            <span className="truncate">{evt.title}</span>
                          </div>
                        ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Today's schedule */}
          {currentStepIndex === 1 && (
            <div className="space-y-4">
              <CapacityBar scheduled={totalScheduled} available={availableHours} />

              {todayEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No events scheduled today.</p>
              ) : (
                <div className="space-y-1 space-y-3">
                  {todayEvents
                    .sort((a, b) => {
                      if (a.isAllDay && !b.isAllDay) return -1;
                      if (!a.isAllDay && b.isAllDay) return 1;
                      return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
                    })
                    .map((evt) => (
                      <div key={evt.id} className="flex items-center gap-2 text-sm py-1">
                        {evt.isAllDay ? (
                          <>
                            <Calendar className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="text-muted-foreground w-24 shrink-0">All day</span>
                          </>
                        ) : (
                          <>
                            <Clock className="h-3 w-3 text-muted-foreground shrink-0" />
                            <span className="text-muted-foreground w-24 shrink-0">
                              {formatTimeLocal(evt.startAt, timeFormat)} –{' '}
                              {formatTimeLocal(evt.endAt, timeFormat)}
                            </span>
                          </>
                        )}
                        <span className="truncate">{evt.title}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Plan your day (schedule unscheduled tasks) */}
          {currentStepIndex === 2 && (
            <div className="space-y-4">
              <CapacityBar scheduled={totalScheduled} available={availableHours} />

              {unscheduledTasks.length === 0 ? (
                <p className="text-sm text-muted-foreground">All tasks are scheduled.</p>
              ) : (
                <div className="space-y-3 space-y-3">
                  {unscheduledTasks.map((task) => {
                    const isScheduled = task.id in scheduledTaskHours;
                    return (
                      <div key={task.id} className="border rounded-md p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{task.title}</span>
                            <Badge
                              variant="outline"
                              className={`text-xs ${PRIORITY_COLORS[task.priority]}`}
                            >
                              {task.priority}
                            </Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {task.estimatedMinutes || 60}m
                          </span>
                        </div>

                        {isScheduled ? (
                          <div className="flex items-center gap-1 text-xs text-green-600">
                            <Check className="h-3 w-3" />
                            Scheduled at {scheduledTaskHours[task.id]}:00
                          </div>
                        ) : schedulingTaskId === task.id ? (
                          <div className="flex items-center gap-2">
                            <Select
                              onValueChange={(v) =>
                                handleScheduleConfirm(task.id, parseInt(v, 10), task)
                              }
                            >
                              <SelectTrigger className="h-8 w-32 text-xs">
                                <SelectValue placeholder="Pick a time" />
                              </SelectTrigger>
                              <SelectContent>
                                {workingHourOptions.map((h) => (
                                  <SelectItem key={h} value={String(h)}>
                                    {h}:00
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 text-xs"
                              onClick={() => setSchedulingTaskId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => setSchedulingTaskId(task.id)}
                          >
                            Schedule
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Set your focus */}
          {currentStepIndex === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                What are your top priorities for today? (max 3)
              </p>

              <div className="flex gap-2">
                <Input
                  value={priorityInput}
                  onChange={(e) => setPriorityInput(e.target.value)}
                  placeholder="e.g., Ship the new feature"
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addPriority();
                    }
                  }}
                  disabled={localPriorities.length >= 3}
                />
                <Button
                  size="sm"
                  onClick={addPriority}
                  disabled={!priorityInput.trim() || localPriorities.length >= 3}
                >
                  Add
                </Button>
              </div>

              {localPriorities.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {localPriorities.map((p, i) => (
                    <Badge key={i} variant="secondary" className="gap-1 py-1 px-2">
                      {p}
                      <button
                        type="button"
                        onClick={() => removePriority(i)}
                        className="ml-1 hover:text-destructive"
                        aria-label={`Remove ${p}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {stepToggle}

        <DialogFooter className="flex justify-between gap-2 sm:justify-between shrink-0">
          <div>
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={goBack}>
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {!isLastStep && (
              <>
                <Button variant="ghost" size="sm" onClick={goNext}>
                  Skip
                </Button>
                <Button size="sm" onClick={goNext}>
                  Next
                </Button>
              </>
            )}
            {isLastStep && (
              <Button size="sm" onClick={handleDone}>
                Done
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
